// PERLIS Landing — Configurateur de coffret.
// Sélection de 3 ou 4 parfums, mapping vers les vrais bundles Shopify (jamais de prix simulé),
// ajout au panier via l'API Ajax Cart réelle, intégré à l'événement panier natif du thème.
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const MAX_FRAGRANCES = 4;
const READY_COUNT = 3;

class PerlisConfigurator extends HTMLElement {
  /** @type {Set<number>} */
  #selected = new Set();
  /** @type {any} */
  #config = null;
  /** @type {boolean} */
  #busy = false;
  /** @type {number | undefined} */
  #feedbackTimeout;
  /** @type {number | undefined} */
  #addedTimeout;
  /** @type {number | null} Variant id of the bundle currently shown as "added" on a CTA, if any. */
  #addedVariantId = null;
  /** @type {HTMLButtonElement | null} The CTA currently showing the "added" confirmation state. */
  #addedCtaEl = null;
  /** @type {Record<string, any>} */
  #refs = {};

  connectedCallback() {
    const configScript = this.querySelector('script[data-perlis-config]');
    if (!configScript) return;

    try {
      this.#config = JSON.parse(configScript.textContent || '{}');
    } catch (error) {
      console.warn('[perlis-configurator] Configuration JSON invalide.', error);
      return;
    }

    this.#refs = {
      progressText: this.querySelector('[data-ref="progressText"]'),
      progressFill: this.querySelector('[data-ref="progressFill"]'),
      fragCards: Array.from(this.querySelectorAll('[data-ref="fragCard"]')),
      helperText: this.querySelector('[data-ref="helperText"]'),
      readyState: this.querySelector('[data-ref="readyState"]'),
      readyPrice: this.querySelector('[data-ref="readyPrice"]'),
      cta3: this.querySelector('[data-ref="cta3"]'),
      upsellBlock: this.querySelector('[data-ref="upsellBlock"]'),
      upsellText: this.querySelector('[data-ref="upsellText"]'),
      upsellCta: this.querySelector('[data-ref="upsellCta"]'),
      completeState: this.querySelector('[data-ref="completeState"]'),
      completePrice: this.querySelector('[data-ref="completePrice"]'),
      cta4: this.querySelector('[data-ref="cta4"]'),
      feedback: this.querySelector('[data-ref="feedback"]'),
    };

    // Mémorise le libellé d'origine de chaque CTA (avant toute manipulation JS) pour
    // pouvoir toujours y revenir de façon fiable, y compris quand une resynchronisation
    // panier doit annuler un état "Ajouté au panier" en cours.
    if (this.#refs.cta3) this.#refs.cta3.dataset.defaultLabel = this.#refs.cta3.textContent.trim();
    if (this.#refs.cta4) this.#refs.cta4.dataset.defaultLabel = this.#refs.cta4.textContent.trim();

    this.#refs.fragCards.forEach((card) => {
      const index = Number(card.dataset.index);
      const fragrance = this.#config.fragrances?.[index];
      if (!fragrance || !fragrance.available) {
        card.dataset.unavailable = 'true';
        card.setAttribute('aria-disabled', 'true');
      }
      card.addEventListener('click', () => this.#toggleFragrance(index));
    });

    this.#refs.cta3?.addEventListener('click', () => this.#handleAddToCart('ready'));
    this.#refs.cta4?.addEventListener('click', () => this.#handleAddToCart('complete'));
    this.#refs.upsellCta?.addEventListener('click', () => this.#acceptUpsell());

    // Resynchronise l'état "Ajouté au panier" avec le panier réel : si le coffret est
    // retiré (icône corbeille du tiroir, quantité ramenée à 0, etc.) ailleurs dans le
    // thème, le bouton doit immédiatement redevenir disponible — sans jamais toucher à
    // la sélection de parfums en cours.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleExternalCartUpdate);

    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleExternalCartUpdate);
    clearTimeout(this.#addedTimeout);
    clearTimeout(this.#feedbackTimeout);
  }

  /**
   * @param {number} index
   */
  #toggleFragrance(index) {
    if (this.#busy) return;

    const card = this.#refs.fragCards[index];
    if (card?.dataset.unavailable === 'true') return;

    if (this.#selected.has(index)) {
      this.#selected.delete(index);
    } else {
      if (this.#selected.size >= MAX_FRAGRANCES) return;
      this.#selected.add(index);
    }

    this.#clearFeedback();
    this.#render();
  }

  /**
   * Détermine, pour une sélection de 3 parfums, l'index du parfum exclu.
   * @returns {number | null}
   */
  #getMissingIndex() {
    if (this.#selected.size !== READY_COUNT) return null;
    for (let i = 0; i < MAX_FRAGRANCES; i += 1) {
      if (!this.#selected.has(i)) return i;
    }
    return null;
  }

  #acceptUpsell() {
    if (this.#busy) return;
    const missingIndex = this.#getMissingIndex();
    if (missingIndex === null) return;
    this.#selected.add(missingIndex);
    this.#clearFeedback();
    this.#render();
    this.#refs.completeState?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  #render() {
    const count = this.#selected.size;

    // Cartes : état sélectionné.
    this.#refs.fragCards.forEach((card, index) => {
      const isSelected = this.#selected.has(index);
      card.setAttribute('aria-pressed', String(isSelected));
    });

    // Progression.
    const total = count >= MAX_FRAGRANCES ? MAX_FRAGRANCES : READY_COUNT;
    const progressPattern = this.dataset.progressLabel || '{current}/{total}';
    if (this.#refs.progressText) {
      this.#refs.progressText.textContent = progressPattern
        .replace('{current}', String(count))
        .replace('{total}', String(total));
    }
    if (this.#refs.progressFill) {
      const pct = Math.min(100, Math.round((count / total) * 100));
      this.#refs.progressFill.style.width = `${pct}%`;
    }

    // Aide (sélection incomplète).
    if (this.#refs.helperText) {
      if (count < READY_COUNT) {
        const remaining = READY_COUNT - count;
        const pattern = this.dataset.helperIncomplete || '';
        this.#refs.helperText.textContent = pattern.replace('{remaining}', String(remaining));
      } else {
        this.#refs.helperText.textContent = '';
      }
    }

    const isReady = count === READY_COUNT;
    const isComplete = count >= MAX_FRAGRANCES;

    this.#setHidden(this.#refs.readyState, !isReady);
    this.#setHidden(this.#refs.completeState, !isComplete);
    this.#setHidden(this.#refs.upsellBlock, !isReady);

    if (isReady) {
      const missingIndex = this.#getMissingIndex();
      const bundle = missingIndex !== null ? this.#config.missingBundles?.[missingIndex] : null;
      this.#renderState(this.#refs.readyPrice, this.#refs.cta3, bundle);

      if (this.#refs.upsellText && bundle) {
        const pattern = this.dataset.upsellText || '';
        this.#refs.upsellText.textContent = pattern.replace('{price}', bundle.upsellDiffMoney || '');
      }
      if (this.#refs.upsellCta) {
        this.#refs.upsellCta.disabled = !bundle || !bundle.available;
      }
    }

    if (isComplete) {
      const bundle = this.#config.completeBundle;
      this.#renderState(this.#refs.completePrice, this.#refs.cta4, bundle);
    }
  }

  /**
   * @param {HTMLElement | null | undefined} priceEl
   * @param {HTMLButtonElement | null | undefined} ctaEl
   * @param {{available?: boolean, variantId?: number, priceMoney?: string} | null | undefined} bundle
   */
  #renderState(priceEl, ctaEl, bundle) {
    const available = !!(bundle && bundle.available && bundle.variantId);
    if (priceEl) {
      priceEl.textContent = available ? bundle.priceMoney : this.dataset.errorUnavailable || '';
    }
    if (ctaEl) {
      // Un CTA en cours d'affichage de la confirmation "Ajouté au panier" reste tel
      // quel tant qu'aucune resynchronisation panier ne l'a explicitement libéré.
      const isShowingAdded = ctaEl === this.#addedCtaEl;
      ctaEl.disabled = (!available || this.#busy) && !isShowingAdded;
      ctaEl.dataset.variantId = available ? String(bundle.variantId) : '';
    }
  }

  /**
   * @param {HTMLElement | null | undefined} el
   * @param {boolean} hidden
   */
  #setHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.setAttribute('hidden', '');
    } else {
      el.removeAttribute('hidden');
    }
  }

  /**
   * @param {'ready' | 'complete'} which
   */
  #handleAddToCart(which) {
    if (this.#busy) return;
    const ctaEl = which === 'ready' ? this.#refs.cta3 : this.#refs.cta4;
    const variantId = ctaEl?.dataset.variantId;
    if (!variantId) {
      this.#showFeedback(this.dataset.errorUnavailable || '', 'error');
      return;
    }
    this.#addToCart(Number(variantId), ctaEl);
  }

  /**
   * @param {number} variantId
   * @param {HTMLButtonElement | null | undefined} ctaEl
   */
  async #addToCart(variantId, ctaEl) {
    this.#busy = true;
    const buttons = [this.#refs.cta3, this.#refs.cta4].filter(Boolean);
    buttons.forEach((btn) => {
      btn.disabled = true;
    });
    // Toujours repartir du libellé d'origine capturé au chargement, jamais du texte
    // actuellement affiché : évite qu'un ajout déclenché pendant qu'un précédent CTA
    // affiche encore "Ajouté au panier" ne fige ce texte comme nouvel "état par défaut".
    const originalLabel = ctaEl?.dataset.defaultLabel ?? (ctaEl ? ctaEl.textContent : '');
    let succeeded = false;
    if (ctaEl) ctaEl.textContent = this.dataset.addingLabel || '';
    this.#showFeedback('', null, { silent: true });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const cartAddUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        const ajaxCart = await this.#refreshCart().catch(() => null);
        if (ajaxCart) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: { didError: true, items: ajaxCart.items, source: 'perlis-configurator', sourceId: this.id || 'perlis-configurator' },
          });
        } else {
          deferred.reject(new Error(data.description || data.message || 'add to cart failed'));
        }
        this.#showFeedback(data.description || data.message || this.dataset.errorGeneric || '', 'error');
        return;
      }

      const ajaxCart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'perlis-configurator',
          sourceId: this.id || 'perlis-configurator',
          didError: false,
        },
      });
      this.#showFeedback(this.dataset.addedLabel || '', 'success');
      succeeded = true;
      this.#flashAdded(ctaEl, originalLabel, variantId);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#showFeedback(this.dataset.errorNetwork || '', 'error');
    } finally {
      this.#busy = false;
      if (ctaEl && !succeeded) ctaEl.textContent = originalLabel;
      this.#render();
    }
  }

  /**
   * Affiche brièvement une confirmation « ✓ Ajouté au panier » sur le bouton cliqué,
   * sans ouvrir le panier ni rediriger, puis restaure son état d'origine. L'état est
   * aussi restauré immédiatement si le coffret correspondant est retiré du panier
   * ailleurs dans le thème (cf. #handleExternalCartUpdate).
   * @param {HTMLButtonElement | null | undefined} ctaEl
   * @param {string} originalLabel
   * @param {number} variantId
   */
  #flashAdded(ctaEl, originalLabel, variantId) {
    if (!ctaEl) return;
    clearTimeout(this.#addedTimeout);
    ctaEl.classList.add('perlis-configurator__cta--added');
    ctaEl.textContent = `✓ ${this.dataset.addedLabel || 'Ajouté au panier'}`;
    this.#addedVariantId = variantId;
    this.#addedCtaEl = ctaEl;
    this.#addedTimeout = window.setTimeout(() => {
      this.#resetAddedState(ctaEl, originalLabel);
    }, 2500);
  }

  /**
   * Ramène un CTA de son état "Ajouté au panier" à son état par défaut, et met à jour
   * l'état de disponibilité/désactivation en fonction de la sélection en cours (sans
   * jamais modifier la sélection elle-même).
   * @param {HTMLButtonElement} ctaEl
   * @param {string} [originalLabel]
   */
  #resetAddedState(ctaEl, originalLabel) {
    clearTimeout(this.#addedTimeout);
    ctaEl.classList.remove('perlis-configurator__cta--added');
    ctaEl.textContent = originalLabel ?? ctaEl.dataset.defaultLabel ?? ctaEl.textContent;
    if (this.#addedCtaEl === ctaEl) {
      this.#addedVariantId = null;
      this.#addedCtaEl = null;
    }
    // Ré-évalue disabled/prix pour la sélection actuelle (elle n'est pas touchée ici).
    this.#render();
  }

  /**
   * Écoute les mises à jour panier déclenchées ailleurs dans le thème (tiroir panier,
   * page panier, quantité ramenée à 0, etc.) pour resynchroniser immédiatement le
   * bouton "Ajouté au panier" quand le coffret correspondant n'est plus dans le panier.
   * @param {CartLinesUpdateEvent} event
   */
  #handleExternalCartUpdate = (event) => {
    // Ignore notre propre dispatch (déclenché depuis #addToCart) : il est déjà géré
    // en ligne par #flashAdded / #resetAddedState.
    if (event.target === this) return;
    if (this.#addedVariantId == null || !this.#addedCtaEl) return;

    const watchedVariantId = this.#addedVariantId;
    const ctaEl = this.#addedCtaEl;

    event.promise
      ?.then(({ detail }) => {
        const items = detail?.items;
        if (!Array.isArray(items)) return;

        const stillInCart = items.some(
          (item) => Number(item.variant_id) === watchedVariantId && Number(item.quantity) > 0
        );

        if (!stillInCart) {
          this.#resetAddedState(ctaEl, ctaEl.dataset.defaultLabel);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[perlis-configurator] Cart sync promise rejected:', error);
      });
  };

  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }
    const cartUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
    const response = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * @param {string} message
   * @param {'success' | 'error' | null} state
   * @param {{silent?: boolean}} [options]
   */
  #showFeedback(message, state, options = {}) {
    if (!this.#refs.feedback) return;
    clearTimeout(this.#feedbackTimeout);

    if (!message) {
      this.#refs.feedback.textContent = '';
      this.#refs.feedback.removeAttribute('data-state');
      this.#refs.feedback.setAttribute('hidden', '');
      return;
    }

    this.#refs.feedback.textContent = message;
    if (state) {
      this.#refs.feedback.dataset.state = state;
    } else {
      this.#refs.feedback.removeAttribute('data-state');
    }
    this.#refs.feedback.removeAttribute('hidden');

    if (!options.silent) {
      this.#feedbackTimeout = window.setTimeout(() => this.#clearFeedback(), 6000);
    }
  }

  #clearFeedback() {
    this.#showFeedback('', null);
  }
}

if (!customElements.get('perlis-configurator')) {
  customElements.define('perlis-configurator', PerlisConfigurator);
}
